import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Input,
  OnChanges,
  Output,
} from '@angular/core';
import { BrowserDimensions } from '@panamax/app-state/lib/models/platform.model';
import { PaymentTransactionsViewModel } from '@usf/payment-types';
import { TransactionRowBaseComponent } from '../transaction-row-base.component';

@Component({
  selector: 'app-transaction-row-desktop',
  templateUrl: './transaction-row-desktop.component.html',
  styleUrls: ['./transaction-row-desktop.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class TransactionRowDesktopComponent
  extends TransactionRowBaseComponent
  implements OnChanges
{
  @Input() browserDimensions: BrowserDimensions;
  @Input() isTouch: boolean;
  @Input() showFullPO = false;
  @Input() maxPOCharLimit = 8;
  @Input() columnSizes: Map<
    TransactionRowDesktopConstants.ColumnNames,
    string | number
  > = new Map();
  @Output() poClickEmitter = new EventEmitter<void>();
  isPoClickable = false;
  @Output()
  payInvoiceClickEmitter = new EventEmitter<PaymentTransactionsViewModel>();

  constructor() {
    super();
  }

  ngOnChanges(): void {
    super.ngOnChanges();
    if (this.vm !== undefined) {
      this.isPoClickable =
        this.showFullPO ||
        (!!this.vm.customerPO &&
          this.vm.customerPO.length > this.maxPOCharLimit);
    }
  }

  onPayTransactionClick() {
    this.payInvoiceClickEmitter.emit(this.vm);
  }

  get truncatedPO() {
    return this.showFullPO || this.vm?.customerPO.length <= this.maxPOCharLimit
      ? this.vm?.customerPO
      : '\u2026' +
          this.vm?.customerPO.substring(
            this.vm?.customerPO.length - this.maxPOCharLimit,
            this.vm?.customerPO.length,
          );
  }

  onCustomerPOClick() {
    if (this.isPoClickable) {
      this.poClickEmitter.emit();
    }
  }
}

export namespace TransactionRowDesktopConstants {
  export enum ColumnNames {
    Checkbox = 'transaction-row-checkbox',
    CustomerInformation = 'transaction-row-customer-information',
    TransactionInformation = 'transaction-row-transaction-information',
    PoNumber = 'transaction-row-po-number',
    IssueDate = 'transaction-row-issue-date',
    Amount = 'transaction-row-amount',
    AmountDue = 'transaction-row-amount-due',
    DueDate = 'transaction-row-due-date',
    OrderNumber = 'transaction-row-order-number',
    PaymentAmount = 'transaction-row-payment-amount',
    Status = 'transaction-row-status',
    PayButton = 'transaction-row-pay-button',
    EllipsisVertical = 'transaction-row-ellipsis-vertical',
    EditAmount = 'transaction-row-edit-amount',
  }

  export function columnSizes(
    shouldHaveEditableAmount = false,
    isReview = false,
    shouldShowButtons = true,
    showFullPO = false,
    showOrderNumber = false,
  ): Map<ColumnNames, string | number> {
    const columnSizes = new Map<ColumnNames, string | number>();

    const orderNumberSize = showOrderNumber ? 1.1 : 0;
    const orderNumberAffected =
      orderNumberSize / (shouldHaveEditableAmount ? 7 : 8);

    const baseSizes = {
      checkbox: 0.4,
      customerInfo: (shouldHaveEditableAmount ? 1.75 : 2) - orderNumberAffected,
      transactionInfo:
        (isReview ? (shouldShowButtons ? 2.6 : 2.9) : 1.6) -
        orderNumberAffected,
      poNumber: isReview ? 1.6 : 1.1,
      poExpanded: 3.0 - orderNumberAffected,
      orderNumber: orderNumberSize,
      issueDate: (isReview ? 1.5 : 1.2) - orderNumberAffected,
      amount: (isReview ? 1.5 : 1.3) - orderNumberAffected,
      amountDue: 1.2 - orderNumberAffected,
      dueDate: 1.1 - orderNumberAffected,
      status: 1.1 - orderNumberAffected,
      paymentAmount: 1.6 - orderNumberAffected,
      payButton: 0.5,
      ellipsisVertical: 0.5,
      editAmount: 1.25,
    };

    const columnsAffectedByPO = (isReview ? 4 : 5) + (showOrderNumber ? 1 : 0);
    const poSizeDiffPerUpdatedColumn =
      (baseSizes.poExpanded - baseSizes.poNumber) / columnsAffectedByPO;

    const adjustForPO = (baseSize: number) =>
      showFullPO ? baseSize - poSizeDiffPerUpdatedColumn : baseSize;

    columnSizes[ColumnNames.Checkbox] = baseSizes.checkbox;
    columnSizes[ColumnNames.CustomerInformation] = baseSizes.customerInfo;
    columnSizes[ColumnNames.TransactionInformation] = baseSizes.transactionInfo;
    columnSizes[ColumnNames.PoNumber] = showFullPO
      ? baseSizes.poExpanded
      : baseSizes.poNumber;
    columnSizes[ColumnNames.IssueDate] = adjustForPO(baseSizes.issueDate);
    columnSizes[ColumnNames.Amount] = adjustForPO(baseSizes.amount);
    columnSizes[ColumnNames.AmountDue] = adjustForPO(baseSizes.amountDue);
    columnSizes[ColumnNames.DueDate] = adjustForPO(baseSizes.dueDate);
    columnSizes[ColumnNames.OrderNumber] = adjustForPO(baseSizes.orderNumber);
    columnSizes[ColumnNames.PaymentAmount] = baseSizes.paymentAmount;
    columnSizes[ColumnNames.Status] = adjustForPO(baseSizes.status);
    columnSizes[ColumnNames.PayButton] = baseSizes.payButton;
    columnSizes[ColumnNames.EllipsisVertical] = baseSizes.ellipsisVertical;
    columnSizes[ColumnNames.EditAmount] = baseSizes.editAmount;

    return columnSizes;
  }
}
